import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AanAfwezigheid } from '../afas-classes/aanafwezigheid.class';
import { AfasProject } from '../afas-classes/afas-project';
import { AfasUser } from '../afas-classes/afas-user';
import { Employees } from '../afas-classes/employees.class';
import { Hourtype } from '../afas-classes/hourtype';
import { Illness } from '../afas-classes/illness.class';
import { AfasLeave } from '../afas-classes/leave.class';
import { PtRealization } from '../afas-classes/ptrealization.class';
import { Realization } from '../afas-classes/realization.class';
import { Rooster } from '../afas-classes/rooster';
import { User } from '../classes/user.class';
import { Utils } from '../utils.class';
import { ApiService } from './api/api.service';
import { AuthorisationService } from './auth/authorisation.service';
import { Features } from './auth/features';

@Injectable({
    providedIn: 'root',
})
export class AfasService {
    constructor(private apiService: ApiService) {}

    public getEmployees() {
        return this.apiService.getCall$<Employees[]>('afas/employees');
    }

    public getMaterieel() {
        return this.apiService.getCall$<Employees[]>('afas/materieel');
    }

    public getHourtypes() {
        return this.apiService.getCall$<Hourtype[]>('afas/hourtypes');
    }

    public getRealization(employeeId: string) {
        return this.apiService.getCall$<Realization[]>('afas/realization', employeeId ? { employeeId } : {});
    }

    public getProfile() {
        return this.apiService.getCallData$<{
            employee: Employees;
            user: User;
            afas_user: AfasUser;
            afas_environment: string;
            afas_test: string;
        }>('afas/profile');
    }

    public getLeaves(fromDate: Date, toDate: Date, employeeId?: string) {
        if (AuthorisationService.hasFeature(Features.AfasLeaves)) {
            const begindate = Utils.newDate(fromDate);
            Utils.setTime(begindate, 0, 0);
            const enddate = Utils.newDate(toDate);
            Utils.setTime(enddate, 23, 59);
            return this.apiService.getCall$<AfasLeave[]>('afas/leaves', {
                begindate: begindate.toISOString(),
                enddate: enddate.toISOString(),
                employeeId: employeeId ?? '',
            });
        }
        return of(null);
    }

    public rooster(fromDate: Date, toDate: Date, employeeId?: string) {
        const begindate = Utils.newDate(fromDate);
        Utils.setTime(begindate, 0, 0);
        const enddate = Utils.newDate(toDate);
        Utils.setTime(enddate, 23, 59);
        return this.apiService.getCall$<Rooster[]>('afas/rooster', {
            begindate: begindate.toISOString(),
            enddate: enddate.toISOString(),
            employeeId: employeeId ?? '',
        });
    }

    public getAanAfwezigheid(fromDate: Date, toDate: Date, employeeId?: string) {
        const begindate = Utils.newDate(fromDate);
        Utils.setTime(begindate, 0, 0);
        const enddate = Utils.newDate(toDate);
        Utils.setTime(enddate, 23, 59);
        return this.apiService.getCall$<AanAfwezigheid[]>('afas/aanafwezigheid', {
            begindate: begindate.toISOString(),
            enddate: enddate.toISOString(),
            employeeId: employeeId ?? '',
        });
    }

    public getOwnAanAfwezigheid(fromDate: Date, toDate: Date, userId?: number) {
        const begindate = Utils.newDate(fromDate);
        Utils.setTime(begindate, 0, 0);
        const enddate = Utils.newDate(toDate);
        Utils.setTime(enddate, 23, 59);
        return this.apiService.getCall$<AanAfwezigheid[]>('afas/own-aanafwezigheid', {
            begindate: begindate.toISOString(),
            enddate: enddate.toISOString(),
            userId: userId ?? '',
        });
    }

    public getIllness(fromDate: Date, toDate: Date, employeeId?: string) {
        if (AuthorisationService.hasFeature(Features.AfasLeaves)) {
            const begindate = Utils.newDate(fromDate);
            Utils.setTime(begindate, 0, 0);
            const enddate = Utils.newDate(toDate);
            Utils.setTime(enddate, 23, 59);
            return this.apiService.getCall$<Illness[]>('afas/illness', {
                begindate: begindate.toISOString(),
                enddate: enddate.toISOString(),
                employeeId: employeeId ?? '',
            });
        }
        return of(null);
    }

    public getProjects(search?: string) {
        let params = {};
        if (search) {
            params = { search };
        }
        return this.apiService.getCall$<AfasProject[]>('afas/projects', params).pipe(
            map(projects => {
                if (projects.data?.length) {
                    const mainProjects = projects.data.filter(p => !p.hoofdproject);
                    mainProjects.forEach(mainProject => {
                        mainProject.projects = projects.data.filter(p => p.hoofdproject === mainProject.projectId);
                        mainProject.projects.forEach(project => {
                            project.mainProject = mainProject.projectId + ' - ' + mainProject.description;
                        });
                    });
                }
                return projects;
            })
        );
    }

    public saveRealization(re: Partial<PtRealization>) {
        re.Ap = true; // Geaccodeerd, moet true dan niet aanpasbaar in profit
        re.Pr = true; // Gereedgemeld, moet true dan niet aanpasbaar in profit
        return this.apiService.postCall$<boolean>('afas/realization', re);
    }

    public getInvoicesByProject(projectId: string) {
        return this.apiService.getCallData$<InvoicePayload[]>('afas/invoices', { projectId });
    }

    public downloadZip(payload: {
        items: { subjectid: number; fileid: string; creditorname: string; invoicenr: string; projectId: string }[];
    }) {
        return this.apiService.postBlobCall<Blob>('afas/download-invoices', payload);
    }

    public afasSync() {
        return this.apiService.getCallData$<boolean>('afas/sync');
    }
}

/* Invoice / dossier item payload */

export interface InvoicePayload {
    boekjaar: number;
    datum: string;
    financieleboeking: boolean;
    geaccordeerd: boolean;
    inkoopfactuur: string;
    omschrijving: string;
    periode: number;
    project: string;
    regelnummer: number;
    subject: Subject;
    filename?: string; // toegevoegd op frontend
}

export interface Subject {
    typeId: number;
    subjectId: number;
    dateCreated: string; // ISO date string
    subject: string;
    comment: string | null;
    status: string;
    number: string | null;
    feV1: string | null;
    feV2: string | null;
    feV3: string | null;
    orgPersId: string;
    contactNr: number;
    toEmployer: boolean;
    toDebtor: boolean;
    toCreditor: boolean;
    toOrgPers: boolean;
    toEmployee: boolean;
    toApplicant: boolean;
    project: string;
    campagne: string | null;
    source: string | null;
    responsible: string | null;
    startDate: string | null;
    endDate: string | null;
    afgehandeld: boolean;
    datumafgehandeld: string | null;
    bron: string | null;
    typeactie: string | null;
    prioriteit: string | null;
    wFstatus: string;
    wFClosed: string | null;
    attachments: Attachment[];
}

export interface Attachment {
    attachmentid: number;
    subjectid: number;
    fileid: string;
    filename: string;
}
